/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { BasicAlertComponent } from '../alerts/basic-alert.component';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { ActivityIndicatorModalComponent } from '../../components/modals/activity-indicator-modal.component';
import { ActiveDocumentsModalComponent } from './active-documents-modal.component';
import { HttpClient } from '@angular/common/http';
import {
  ActiveDocType,
  ActiveDocumentsService,
} from '../../services/active-documents.service';
import { UserService } from '../../services/user.service';
import { ErrorService } from '../../services/error.service';

// DEBUG ONLY
import { delay } from 'rxjs/operators';

@Component({
  selector: 'app-active-documents',
  templateUrl: './active-documents.component.html',
  styleUrls: ['./active-documents.component.less'],
  standalone: false,
})
export class ActiveDocumentsComponent implements OnInit {
  activeDocsList: ActiveDocType[] = [];
  direction = 'asc';
  column = '';
  type = 'string';
  displayPagination = true;
  page = 1;
  initialPageSize = 10;
  pageSize = 10;
  pageSizeList: any = [
    { size: 5 },
    { size: 10 },
    { size: 25 },
    { size: 50 },
    { size: 100 },
  ];
  documentExpiryWarningTime = 30;
  documentKillWarningTime = 30;
  noDocuments = false;
  docDetailsClicked = false;

  // Temp document upload
  showUpload = false;
  uploadFile: FileList | null = null;
  @ViewChild('fileSelect') fileSelect: ElementRef;

  private xdaTranslatePipe: XdaTranslate;
  constructor(
    private modalService: NgbModal,
    private xdaStringsService: XdaStringsService,
    private tooltipConfig: NgbTooltipConfig,
    private http: HttpClient,
    private activeDocService: ActiveDocumentsService,
    private userService: UserService,
    private errorService: ErrorService,
  ) {
    this.tooltipConfig.tooltipClass = 'xrx-tooltip';
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
  }

  ngOnInit(): void {
    const isMDUp = window.matchMedia('(min-width: 768px)');

    // set initialPageSize to 5 for screen sizes below MD
    if (!isMDUp.matches) {
      this.initialPageSize = 5;
      this.pageSize = 5;
    }
    this.getActiveDocsList();
  }

  async getActiveDocsList() {
    const modalActivityRef = this.modalService.open(
      ActivityIndicatorModalComponent,
      {
        windowClass: 'xrx-modal transparent',
        centered: true,
        backdrop: 'static',
      },
    );

    // await this.http.get('activeDocs.json').pipe(delay(2000)).toPromise()
    await this.http
      .get<ActiveDocType[]>(
        `${this.activeDocService.getRPostBaseUrl()}docs?senderAddress=${encodeURIComponent(this.userService.user?.email || '')}`,
      )
      .toPromise()
      .then((json) => {
        this.activeDocsList = json;

        if (this.activeDocsList.length < 1) {
          this.noDocuments = true;
        }

        if (this.activeDocsList.length <= this.pageSize) {
          this.displayPagination = false;
        }

        this.activeDocsList.forEach((d) => {
          // add additional document values needed for UI
          d.documentStatus = this.activeDocService.mapDocumentStatus(
            d.documentOptions.expiryTime,
          );
          d.documentCreated = new Date(d.created).getTime();
          d.documentCreatedFormatted = this.activeDocService.formatDate(
            d.created,
          );
          d.documentCreatedTimeFormatted = this.activeDocService.formatTime(
            d.created,
          );
          d.documentExpiryDate = new Date(
            d.documentOptions.expiryTime,
          ).getTime();
          d.documentExpiryDateFormatted = this.activeDocService.formatDate(
            d.documentOptions.expiryTime,
          );
          d.documentExpiryTimeFormatted = this.activeDocService.formatTime(
            d.documentOptions.expiryTime,
          );
          d.documentExpiresSoon = this.activeDocService.isDocumentExpiringSoon(
            d,
            this.documentExpiryWarningTime,
          );
          d.documentDeadSoon = this.isDocumentDeadSoon(
            d,
            this.documentKillWarningTime,
          );
          d.documentSecurityLevel = this.activeDocService.mapSecurityLevel(
            d.documentOptions.distRestriction,
            d.documentOptions.readerEmail,
          );
        });
        modalActivityRef.close();
      })
      .catch((error) => {
        console.log(error);
        this.errorService.displayGeneralError().result.then(() => {
          modalActivityRef.close();
        });
      });
  }

  isDocumentDeadSoon(d: any, interval: number): boolean {
    const now = new Date().setHours(0, 0, 0, 0);

    // kill date is 30 days after expire date
    // add 30 days to expire date
    const killDate = new Date(d.documentOptions.expiryTime);
    killDate.setDate(killDate.getDate() + 30);

    let msDiff, dayDiff;

    // difference between killDate and today (in milliseconds)
    msDiff = killDate.setHours(0, 0, 0, 0) - now;
    dayDiff = msDiff > 0 ? msDiff / (24 * 60 * 60 * 1000) : 0; // if msDiff is negative it should have been killed arleady so set to 0

    // if dayDiff is less than interval set documentKillToolTip for the document
    if (dayDiff <= interval) {
      d.documentKillToolTip = `${this.xdaTranslatePipe.transform('SDE_DAYS_BEFORE_DELETION')}: ${Math.floor(dayDiff)}`;
      return true;
    } else {
      return false;
    }
  }

  expireDoc(id: string) {
    // open confirm modal
    const modalRef = this.modalService.open(BasicAlertComponent);
    const doc = this.activeDocsList.find((x) => x.id == id);
    modalRef.componentInstance.title = 'SDE_CONFIRM_SET_DOCUMENT';
    modalRef.componentInstance.body = doc?.fileName;
    modalRef.componentInstance.buttonText = 'SDE_CANCEL';
    modalRef.componentInstance.okButtonText = 'SDE_EXPIRE';

    modalRef.result.then(
      (data) => {
        // if user confirmed expire, make API call to RPost to expire document
        if (data === 'Ok' && doc) {
          doc.documentOptions.expiryTime = new Date().toISOString();
          const formData = new FormData();
          formData.append('document', JSON.stringify(doc));
          this.http
            .put(
              `${this.activeDocService.getRPostBaseUrl()}docs/${doc.id}`,
              formData,
            )
            .toPromise()
            .then((result) => {
              this.getActiveDocsList();
            })
            .catch((error) => {
              console.log(error);
              // probably want a failed update error here instead?
              this.errorService.displayGeneralError();
            });
        }
      },
      () => {},
    );
  }

  deleteDoc(id: string) {
    // open confirm modal
    const modalRef = this.modalService.open(BasicAlertComponent);
    const doc = this.activeDocsList.find((x) => x.id == id);
    modalRef.componentInstance.title = 'SDE_CONFIRM_DELETE_DOCUMENT3';
    modalRef.componentInstance.body = doc?.fileName;
    modalRef.componentInstance.buttonText = 'SDE_CANCEL';
    modalRef.componentInstance.dangerButtonText = 'SDE_DELETE';

    modalRef.result.then(
      (data) => {
        // if user confirmed delete, remove the document from activeDocsList make the API call to RPost to kill the document.
        if (data === 'Delete' && doc) {
          this.http
            .delete(`${this.activeDocService.getRPostBaseUrl()}docs/${doc.id}`)
            .toPromise()
            .then(() => {
              this.activeDocsList.splice(Number(id), 1);
              this.getActiveDocsList(); // TODO: is this needed since it's already been removed from activeDocsList?
            })
            .catch((error) => {
              console.log(error);
              // probably want a failed delete error here instead?
              this.errorService.displayGeneralError();
            });
        }
      },
      () => {},
    );
  }

  viewDocDetails(id: string) {
    // protect against multiple clicks
    if (!this.docDetailsClicked) {
      this.docDetailsClicked = true;
      // get document details from rpost
      this.http
        .get(`${this.activeDocService.getRPostBaseUrl()}doc/${id}`)
        .toPromise()
        .then((result) => {
          const activeDocsModalRef = this.modalService.open(
            ActiveDocumentsModalComponent,
            { windowClass: 'xrx-modal', modalDialogClass: 'modal-lg' },
          );
          activeDocsModalRef.componentInstance.doc = result;
          activeDocsModalRef.result.then(
            (data: string) => {
              this.getActiveDocsList();
              this.docDetailsClicked = false;
            },
            () => {
              this.docDetailsClicked = false;
            },
          );
        })
        .catch((error) => {
          console.log(error);
          // probably want a failed delete error here instead?
          this.errorService.displayGeneralError();
          this.docDetailsClicked = false;
        });
    }
  }

  setSortParams(param: any) {
    this.direction = param.direction;
    this.column = param.column;
    this.type = param.type;
  }

  // temp functionality until DRM workflow function is implemented
  async createRPD() {
    const json = await this.http
      .get('createDoc.json')
      .pipe(delay(2000))
      .toPromise();
    if (json === undefined || !this.uploadFile) {
      return;
    }

    const modalActivityRef = this.modalService.open(
      ActivityIndicatorModalComponent,
      {
        windowClass: 'xrx-modal transparent',
        centered: true,
        backdrop: 'static',
      },
    );

    const formData = new FormData();
    formData.append('file', this.uploadFile?.[0]);
    formData.append('document', JSON.stringify(json));
    formData.append('senderAddress', this.userService.user?.email || '');
    await this.http
      .post(`${this.activeDocService.getRPostBaseUrl()}create`, formData)
      .toPromise()
      .then(() => {
        modalActivityRef.close();
        this.uploadFile = null;
        this.fileSelect.nativeElement.value = null;
        this.getActiveDocsList();
      })
      .catch((error) => {
        console.log(error);
        this.errorService.displayGeneralError().result.then(() => {
          modalActivityRef.close();
        });
      });
  }
}
